import React, { useState } from "react";
import {
  TransformWrapper,
  TransformComponent,
  useControls,
} from "react-zoom-pan-pinch";
import {
  FileText,
  RefreshCw,
  LogOut,
  Loader2,
  FileQuestion,
  AlertCircle,
  ExternalLink,
  ImageOff,
  MousePointer2,
  ZoomIn,
  ZoomOut,
  RotateCcw,
} from "lucide-react";
import { ChatPanel } from "./ChatPanel";

interface RunInfo {
  runId: string;
  runName?: string;
}

interface CellReference {
  cell: string;
  sheet: string;
  value: string | number | null;
}

interface SourceImage {
  file_name: string;
  page_number: number;
  image_url: string;
  value?: number | string;
  formula?: string;
}

interface ReferenceViewerProps {
  runInfo: RunInfo;
  selectedCell: CellReference | null;
  sourceData: {
    cell_id?: string;
    images?: SourceImage[];
    notFound?: boolean;
    error?: boolean;
  } | null;
  loading: boolean;
  loadingData?: boolean;
  dataLoaded?: boolean;
  onRefresh: () => void;
  onSignOut?: () => void;
  userEmail?: string;
}

// Zoom controls component
function ZoomControls() {
  const { zoomIn, zoomOut, resetTransform } = useControls();

  return (
    <div className="absolute bottom-3 right-3 flex items-center gap-1 bg-white/90 backdrop-blur-sm rounded-lg shadow-sm border border-gray-200 p-1">
      <button
        onClick={() => zoomIn()}
        className="p-1.5 hover:bg-gray-100 rounded transition-colors"
        title="Zoom in"
      >
        <ZoomIn className="w-4 h-4 text-gray-600" />
      </button>
      <button
        onClick={() => zoomOut()}
        className="p-1.5 hover:bg-gray-100 rounded transition-colors"
        title="Zoom out"
      >
        <ZoomOut className="w-4 h-4 text-gray-600" />
      </button>
      <div className="w-px h-4 bg-gray-200" />
      <button
        onClick={() => resetTransform()}
        className="p-1.5 hover:bg-gray-100 rounded transition-colors"
        title="Reset zoom"
      >
        <RotateCcw className="w-4 h-4 text-gray-600" />
      </button>
    </div>
  );
}

// Source card component with zoom/pan
function SourceCard({ image }: { image: SourceImage }) {
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  return (
    <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
      {/* Card Header */}
      <div className="flex items-center gap-3 px-4 py-3">
        <div className="w-8 h-8 rounded-lg bg-purple-50 flex items-center justify-center flex-shrink-0">
          <FileText className="w-4 h-4 text-purple-500" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">
            {image.file_name}
          </p>
          <p className="text-xs text-gray-400">Page {image.page_number}</p>
        </div>
        <button
          onClick={() => window.open(image.image_url, "_blank")}
          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
          title="Open in new tab"
        >
          <ExternalLink className="w-4 h-4" />
        </button>
      </div>

      {/* Value and Formula */}
      {(image.value !== undefined || image.formula) && (
        <div className="px-4 pb-3 space-y-2">
          {image.value !== undefined && (
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-gray-500">Value:</span>
              <span className="text-sm font-semibold text-gray-900 bg-purple-50 px-2 py-0.5 rounded">
                {image.value}
              </span>
            </div>
          )}
          {image.formula && (
            <div className="flex items-start gap-2">
              <span className="text-xs font-medium text-gray-500 mt-0.5">Formula:</span>
              <code className="text-xs font-mono text-purple-700 bg-purple-50 px-2 py-1 rounded break-all">
                {image.formula}
              </code>
            </div>
          )}
        </div>
      )}

      {/* Image Preview with Zoom/Pan */}
      <div className="px-4 pb-4">
        <div className="bg-gray-50 rounded-lg overflow-hidden relative">
          {imageError ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-400">
              <ImageOff className="w-8 h-8 mb-3 opacity-40" />
              <p className="text-sm">Preview not available</p>
              <button
                onClick={() => window.open(image.image_url, "_blank")}
                className="mt-3 text-xs text-purple-600 hover:text-purple-700 font-medium"
              >
                Open directly
              </button>
            </div>
          ) : (
            <>
              {!imageLoaded && (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-5 h-5 animate-spin text-gray-300" />
                </div>
              )}
              <div className={imageLoaded ? "" : "hidden"}>
                <TransformWrapper
                  initialScale={1}
                  minScale={0.5}
                  maxScale={5}
                  centerOnInit
                  wheel={{ step: 0.1 }}
                  doubleClick={{ mode: "zoomIn" }}
                >
                  <div className="relative" style={{ height: "350px" }}>
                    <TransformComponent
                      wrapperStyle={{
                        width: "100%",
                        height: "100%",
                      }}
                      contentStyle={{
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <img
                        src={image.image_url}
                        alt={`Preview of ${image.file_name}`}
                        className="max-w-full max-h-full object-contain"
                        onLoad={() => setImageLoaded(true)}
                        onError={() => setImageError(true)}
                      />
                    </TransformComponent>
                    <ZoomControls />
                  </div>
                </TransformWrapper>
              </div>

              {/* Help text */}
              {imageLoaded && (
                <div className="px-3 py-2 bg-gray-100/50 text-center">
                  <p className="text-[10px] text-gray-400">
                    Scroll to zoom • Drag to pan • Double-click to zoom in
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export function ReferenceViewer({
  runInfo,
  selectedCell,
  sourceData,
  loading,
  loadingData,
  dataLoaded: _dataLoaded,
  onRefresh,
  onSignOut,
  userEmail,
}: ReferenceViewerProps) {
  const hasSource =
    sourceData &&
    !sourceData.notFound &&
    !sourceData.error &&
    sourceData.images &&
    sourceData.images.length > 0;

  const sourceCount = sourceData?.images?.length || 0;

  return (
    <div className="flex flex-col h-screen bg-white font-sans">
      {/* Header */}
      <header className="flex-shrink-0 px-4 py-3 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h1 className="text-base font-semibold text-gray-900">Rima</h1>
            <button
              onClick={onRefresh}
              disabled={loading || !selectedCell}
              className="p-1.5 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              title="Refresh"
            >
              <RefreshCw
                className={`w-4 h-4 text-gray-400 ${loading ? "animate-spin" : ""}`}
              />
            </button>
          </div>
          <div className="flex items-center gap-2">
            {userEmail && (
              <span className="text-xs text-gray-400 truncate max-w-[100px]">
                {userEmail}
              </span>
            )}
            {onSignOut && (
              <button
                onClick={onSignOut}
                className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                title="Sign out"
              >
                <LogOut className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Data Loading Overlay */}
      {loadingData && (
        <div className="absolute inset-0 bg-white/95 z-50 flex items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="w-6 h-6 text-purple-500 animate-spin" />
            <p className="text-sm text-gray-500">Loading...</p>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        {/* Selected Cell Info */}
        {selectedCell && (
          <div className="px-4 py-4 border-b border-gray-50">
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-semibold text-gray-900 font-mono">
                {selectedCell.cell}
              </span>
              <span className="text-sm text-gray-400">
                {selectedCell.sheet}
              </span>
            </div>
            {selectedCell.value !== null && selectedCell.value !== "" && (
              <p className="mt-1 text-sm text-gray-500">
                Value: <span className="font-medium text-gray-700 font-mono">{String(selectedCell.value)}</span>
              </p>
            )}
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex-1 flex items-center justify-center py-20">
            <div className="flex flex-col items-center gap-3">
              <Loader2 className="w-6 h-6 animate-spin text-gray-300" />
              <span className="text-sm text-gray-400">Finding sources...</span>
            </div>
          </div>
        )}

        {/* No Cell Selected */}
        {!loading && !selectedCell && (
          <div className="flex-1 flex flex-col items-center justify-center px-6 py-20">
            <div className="w-12 h-12 rounded-full bg-gray-50 flex items-center justify-center mb-4">
              <MousePointer2 className="w-6 h-6 text-gray-300" />
            </div>
            <p className="text-base font-medium text-gray-900 mb-1">Select a cell</p>
            <p className="text-sm text-gray-400 text-center">
              Click on any cell to view its source references
            </p>
          </div>
        )}

        {/* No Source Found */}
        {!loading && selectedCell && sourceData?.notFound && (
          <div className="flex-1 flex flex-col items-center justify-center px-6 py-20">
            <div className="w-12 h-12 rounded-full bg-gray-50 flex items-center justify-center mb-4">
              <FileQuestion className="w-6 h-6 text-gray-300" />
            </div>
            <p className="text-base font-medium text-gray-900 mb-1">No source found</p>
            <p className="text-sm text-gray-400 text-center">
              This cell doesn't have a mapped reference
            </p>
          </div>
        )}

        {/* Error State */}
        {!loading && selectedCell && sourceData?.error && (
          <div className="flex-1 flex flex-col items-center justify-center px-6 py-20">
            <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mb-4">
              <AlertCircle className="w-6 h-6 text-red-400" />
            </div>
            <p className="text-base font-medium text-gray-900 mb-1">Failed to load</p>
            <p className="text-sm text-gray-400 text-center mb-4">
              Something went wrong
            </p>
            <button
              onClick={onRefresh}
              className="text-sm font-medium text-purple-600 hover:text-purple-700"
            >
              Try again
            </button>
          </div>
        )}

        {/* Source Cards */}
        {!loading && hasSource && (
          <div className="p-4 space-y-3">
            <p className="text-xs font-medium text-gray-400 uppercase tracking-wide px-1">
              {sourceCount} source{sourceCount !== 1 ? "s" : ""}
            </p>
            {sourceData.images!.map((image, index) => (
              <SourceCard
                key={`${image.file_name}-${image.page_number}-${index}`}
                image={image}
              />
            ))}
          </div>
        )}
      </div>

      {/* Chat Panel */}
      <ChatPanel runId={runInfo.runId} />

      {/* Footer */}
      <footer className="flex-shrink-0 py-2 text-center border-t border-gray-50">
        <span className="text-[10px] text-gray-300 font-mono">Rima v1.0</span>
      </footer>
    </div>
  );
}
